import { defineStore } from "pinia";
import { CoinBalanceRecordEntityType } from "@/types/coin";
import i18n from "@/locales";
import {
  getRankServerDtStart,
  useCoinRankItemPage,
  useCoinRankPage,
  useOverallCoinRankPage,
} from "@/composable/rank";
import moment from "moment-timezone";
import { getServerTime } from "@/modules/common";

export const useCoinRank = defineStore("coinRank", () => {
  // 历史榜单列表（实体中不含表单项）
  const {
    historyCoinRankList,
    getOrFetchHistoryCoinRank,
    updateHistoryCoinRankAndReplyMap,
    updateHistoryCoinRankMap,
  } = useCoinRankPage();
  // 榜单项/子榜单
  const {
    getSubRank,
    getOrFetchCoinRankByEntityType,
    fetchRankItem,
    currentCoinRank,
    getOrFetchCurrentRank,
    currentCoinRankDtStart,
    getOrFetchUniCoinRank,
    uniCoinRankMap,
  } = useCoinRankItemPage();

  const {
    nextCursor,
    overallCoinRankList,
    getOrFetchOverallCoinRank,
    updateOverallCoinRankMap,
    fetchNewerOverallCoinRank,
    fetchOlderOverallCoinRank,
  } = useOverallCoinRankPage();

  /**
   * 根据开始日期获取榜单（不含表单项）
   * @param dtStart
   */
  const getRankByDtStart = (dtStart: string) => {
    return historyCoinRankList.value.find(
      (item) => getRankServerDtStart(item.dtStart) === dtStart
    );
  };

  const getRankById = (id: number) =>
    historyCoinRankList.value.find((item) => item.id === id);

  /**
   * 获取或拉取子榜单
   * @param id
   * @param entityType
   * @param force
   */
  const getOrFetchSubRank = async (
    id: number,
    entityType: CoinBalanceRecordEntityType,
    force = false
  ) => {
    return getOrFetchCoinRankByEntityType(id, entityType, force).then(
      (rankList) => updateHistoryCoinRankAndReplyMap([rankList])
    );
  };

  const getOrFetchUniRank = async (id: number, force = false) => {
    const rankList = await getOrFetchUniCoinRank(id, force);
    updateHistoryCoinRankAndReplyMap([rankList]);
  };

  const getOrFetchCurrentRankAndUpdateMap = async (force?: boolean) => {
    await getOrFetchCurrentRank(force);
    updateHistoryCoinRankMap([currentCoinRank.value!]);
  };
  const fetchIfCurrentRankExpired = async () => {
    if (!currentCoinRank.value) return;
    const today = moment().tz("Asia/Hong_Kong");
    const endTime = moment(currentCoinRank.value!.dtEnd).tz("Asia/Hong_Kong");
    const isExpired = today.isAfter(endTime);
    // 距离上期榜单超过15天，且上次检查时间超过1小时
    if (isExpired) {
      await getOrFetchCurrentRankAndUpdateMap(true);
    }
  };

  return {
    currentCoinRank,
    historyCoinRankList,
    currentCoinRankDtStart,
    uniCoinRankMap,
    overallCoinRankList,
    overallNextCursor: nextCursor,
    getOrFetchOverallCoinRank,
    updateOverallCoinRankMap,
    fetchNewerOverallCoinRank,
    fetchOlderOverallCoinRank,
    getSubRank,
    getOrFetchHistoryCoinRank,
    getRankByDtStart,
    getRankById,
    getOrFetchSubRank,
    fetchRankItem,
    getOrFetchCurrentRank: getOrFetchCurrentRankAndUpdateMap,
    fetchIfCurrentRankExpired,
    getOrFetchUniRank,
  };
});

function ordinal(num: number) {
  if (num < 1) {
    return null; // 输入无效
  }
  if (num % 100 >= 11 && num % 100 <= 13) {
    return num + "th"; // 对于11、12、13特殊处理
  }

  switch (num % 10) {
    case 1:
      return num + "st";
    case 2:
      return num + "nd";
    case 3:
      return num + "rd";
    default:
      return num + "th";
  }
}

export const ordinalText = (num: number) => {
  const isChinese = i18n.global.locale.value === "zh-CN";
  return isChinese ? `第${num}名` : ordinal(num);
};

export const shouldHideHalfText = (dtStart: string) =>
  getServerTime(dtStart).isAfter(moment("2024-05-30").tz("Asia/Hong_Kong"));
